import { Link } from "@/lib/router-compat";
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  ClipboardList, Compass, UtensilsCrossed, ArrowRight,
  CalendarCheck, MapPin, HeartHandshake, ShieldCheck,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' as const } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};

/* ── Three simple steps ────────────────────────────────── */
const steps = [
  {
    icon: ClipboardList,
    title: 'Tell us about yourself',
    desc: 'A short, guided introduction — your city, your intentions, and the kind of company you enjoy. A few minutes, one question at a time.',
  },
  {
    icon: Compass,
    title: 'Discover your evening',
    desc: 'We suggest the experience that fits you — a singles dinner, a social table, or a professional evening — in a city we serve.',
  },
  {
    icon: UtensilsCrossed,
    title: 'Meet in person',
    desc: 'Reserve your seat, arrive at a hand-selected venue, and spend the evening in real conversation. We handle the details.',
  },
];

export const ThreeSteps = () => (
  <section id="how-it-works" className="py-20 md:py-28 px-4">
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      variants={stagger}
      className="max-w-5xl mx-auto"
    >
      <motion.div variants={fadeInUp} className="text-center mb-12 md:mb-16">
        <p className="text-xs uppercase tracking-[0.2em] text-primary/70 font-semibold mb-3">How Amorette Works</p>
        <h2 className="font-display text-2xl md:text-4xl font-semibold text-foreground leading-tight">
          Three Steps to a{' '}
          <span className="text-gold-glow">Worthwhile</span>{' '}
          Evening
        </h2>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6 md:gap-8">
        {steps.map((s, i) => (
          <motion.div
            key={s.title}
            variants={fadeInUp}
            className="relative rounded-2xl border border-border/50 bg-card/60 backdrop-blur-sm px-6 py-8 text-center"
          >
            <span className="absolute top-5 left-6 font-display text-sm text-primary/50">0{i + 1}</span>
            <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-primary/10 flex items-center justify-center">
              <s.icon className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-display text-lg font-semibold text-foreground mb-2">{s.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>

      <motion.div variants={fadeInUp} className="text-center mt-10">
        <Link to="/how-it-works" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
          See the full journey <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </motion.div>
  </section>
);

/* ── What to expect ────────────────────────────────────── */
const expectations = [
  {
    icon: CalendarCheck,
    title: 'A confirmed seat',
    desc: 'Your reservation is confirmed before the evening — never a maybe. If plans change, clear cancellation terms apply.',
  },
  {
    icon: MapPin,
    title: 'A venue revealed in advance',
    desc: 'Every venue is vetted for atmosphere and quality. You receive the name and address 12 hours before your experience.',
  },
  {
    icon: HeartHandshake,
    title: 'Mutual interest, handled gracefully',
    desc: 'After the evening, you can privately note who you’d like to see again. Contact details are shared only when the interest is mutual.',
  },
  {
    icon: ShieldCheck,
    title: 'Verified members',
    desc: 'Every member confirms their identity before attending, so the people at your table are who they say they are.',
  },
];

export const WhatToExpect = () => (
  <section className="py-16 md:py-24 px-4 bg-secondary/30">
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-40px' }}
      variants={stagger}
      className="max-w-5xl mx-auto"
    >
      <motion.div variants={fadeInUp} className="text-center mb-12 md:mb-16">
        <p className="text-xs uppercase tracking-[0.2em] text-primary/70 font-semibold mb-3">What to Expect</p>
        <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
          Considered,{' '}
          <span className="text-gold-glow">Start to Finish</span>
        </h2>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
        {expectations.map((e) => (
          <motion.div key={e.title} variants={fadeInUp} className="text-center px-4 py-6">
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
              <e.icon className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-display text-base font-semibold text-foreground mb-2">{e.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{e.desc}</p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  </section>
);

/* ── Membership band ───────────────────────────────────── */
export const MembershipBand = () => (
  <section className="py-16 md:py-20 px-4">
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      variants={fadeInUp}
      className="max-w-3xl mx-auto text-center rounded-3xl border border-primary/20 bg-card/60 backdrop-blur-sm px-6 py-10 md:py-12 shadow-card"
    >
      <p className="text-xs uppercase tracking-[0.2em] text-primary/70 font-semibold mb-3">Membership</p>
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4 leading-tight">
        One Membership.{' '}
        <span className="text-gold-glow">Every Evening.</span>
      </h2>
      <p className="text-base text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
        Membership opens the door to every Amorette experience — singles dinners, social tables,
        and professional evenings. Reserve seats as they suit you; each evening is priced
        individually and shown clearly before you confirm.
      </p>
      <Link to="/pricing">
        <Button variant="outline" className="rounded-full h-11 px-6 text-sm font-semibold border-primary/30 bg-secondary/50 gap-2">
          View Membership Options
          <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
    </motion.div>
  </section>
);

/* ── FAQ ───────────────────────────────────────────────── */
const faqs = [
  {
    q: 'What is Amorette?',
    a: 'Amorette arranges curated dinner experiences that bring people together in person — for romance, friendship, or professional connection. You tell us what you’re looking for, and we suggest the evening that fits.',
  },
  {
    q: 'Is Amorette a dating app?',
    a: 'No. There is no swiping and no endless messaging. Introductions happen at the table, in person. Afterward, you can privately note who you’d like to see again — contact details are shared only when the interest is mutual.',
  },
  {
    q: 'How is my evening chosen?',
    a: 'Your guided introduction covers your city, your intentions, and your preferences. We use it to suggest the experience that suits you — a singles dinner, a social table, or a professional evening. It’s a recommendation, never a score or a judgment.',
  },
  {
    q: 'What does an evening cost?',
    a: 'Each experience is priced individually and the full price — including what’s covered — is shown before you reserve. Membership options are on our Membership page; your guided introduction is always free.',
  },
  {
    q: 'Is dinner included in the price?',
    a: 'Your reservation confirms your seat at a curated table. Meals and drinks are paid directly at the restaurant, so you order whatever you like. The reservation price is always shown before you confirm.',
  },
  {
    q: 'Can I attend on my own?',
    a: 'Absolutely — most guests arrive on their own. Every seat at the table was reserved by someone who came to meet new people.',
  },
  {
    q: 'What happens before the evening?',
    a: 'Once your seat is confirmed you receive your evening details, and the venue is revealed 12 hours before — enough time to plan, never enough to spoil the surprise.',
  },
  {
    q: 'Where is Amorette available?',
    a: 'We open cities as their communities grow. Our Cities page shows where evenings are running and where we’re accepting interest. If your city isn’t listed, you can join its First 500 founding list or interest list and we’ll let you know when it opens.',
  },
  {
    q: 'What happens after the evening?',
    a: 'Within two days you can share private feedback and note anyone you’d like to see again. When the feeling is mutual, we introduce you properly — no public profiles, no unsolicited messages.',
  },
];

export const HomeFAQ = () => (
  <section className="py-20 md:py-28 px-4">
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      variants={stagger}
      className="max-w-2xl mx-auto"
    >
      <motion.div variants={fadeInUp} className="text-center mb-10 md:mb-14">
        <p className="text-xs uppercase tracking-[0.2em] text-primary/70 font-semibold mb-3">Questions</p>
        <h2 className="font-display text-2xl md:text-4xl font-semibold text-foreground leading-tight">
          Everything You’d Like{' '}
          <span className="text-gold-glow">to Know</span>
        </h2>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-border/50">
              <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-foreground hover:text-primary py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>

      <motion.div variants={fadeInUp} className="text-center mt-8">
        <Link to="/faq" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
          Browse all questions <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </motion.div>
  </section>
);
